import type { ReactNode } from 'react';
import { Text, ToggleButton, ToggleButtonGroup } from '@backstage/ui';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import type {
  FieldValue,
  FieldValues,
  RangeBound,
} from '@internal/plugin-waadoo-config-common';
import { NumberInput } from '../../waadooForm/NumberInput';
import { branchTier } from '../branchKind';
import { projectEnvironmentsTranslationRef } from '../translation';
import { ConfigurationRow } from './ConfigurationRow';

/** The field ids of a configuration of shape `C`. */
export type ConfigurationFieldOf<C extends FieldValues> = keyof C & string;

/** What the rows of a type's editor are built from: its draft and its rules. */
export interface ConfigurationRowsInput<C extends FieldValues> {
  /** The editor's `useId`: the prefix of the rows' label ids. */
  idPrefix: string;
  /** The caller's bounds (`configurationBounds` of the engine). */
  ranges: Readonly<Record<string, RangeBound>>;
  draft: C;
  current: C;
  /** The reference's configuration (« Comparer avec »), once read. */
  reference: C | undefined;
  /** The reference's environment, and what the tab calls it (`dev-*`). */
  referenceEnvironment: string | undefined;
  referenceLabel: string | undefined;
  /** The fields the caller may not change (`lockedFieldIds` of the engine). */
  locked: ReadonlySet<ConfigurationFieldOf<C>>;
  label(field: ConfigurationFieldOf<C>): string;
  /** The unit a number is typed in (`Mio`, `Gio`, `%`…), if any. */
  unit(field: ConfigurationFieldOf<C>): string | undefined;
  /** A value as a row and the panel say it (`5 Gio`, `oui`). */
  valueText(
    field: ConfigurationFieldOf<C>,
    value: C[ConfigurationFieldOf<C>],
  ): string;
  isDisabled(field: ConfigurationFieldOf<C>): boolean;
  /** Whether the field is marked invalid (a problem names or reads it). */
  invalid(field: ConfigurationFieldOf<C>): boolean;
  /** The problems said under a row of these fields. */
  problems(...fields: Array<ConfigurationFieldOf<C>>): string[];
  /** Writes a value in the draft. */
  set(field: ConfigurationFieldOf<C>, value: FieldValue): void;
}

/** The choices of a segmented control. */
export interface ConfigurationChoice<K extends string> {
  label: string;
  selected: K;
  items: Array<{ id: K; label: string }>;
  disabled: boolean;
  onSelect: (id: K) => void;
}

/** The parts of the rows, shared by every type's editor. */
export interface ConfigurationRows<C extends FieldValues>
  extends ConfigurationRowsInput<C> {
  /** A label id of the editor, by key. */
  ids(key: string): string;
  /** The lock's label: « Réservé aux administrateurs ». */
  lockedLabel: string;
  /** What a row says under its label, its parts joined by « · ». */
  hintText(...parts: Array<string | undefined>): ReactNode;
  /** « était … » when one of the fields differs from the current value. */
  was(...fields: Array<ConfigurationFieldOf<C>>): string | undefined;
  /**
   * The reference's value after a field, in its tier's ink, when it differs
   * from what the form shows: `text` reads a configuration the way the row
   * shows it. Nothing without a readable reference.
   */
  referenceText(text: (config: C) => string): ReactNode;
  /** `text` for `referenceText`: the fields' values, joined by « / ». */
  fieldsText(...fields: Array<ConfigurationFieldOf<C>>): (config: C) => string;
  /** The number input of a field, within the caller's bounds. */
  number(
    field: ConfigurationFieldOf<C>,
    labelledBy: string,
    min?: number,
  ): ReactNode;
  /** A reserved / ceiling pair in one row. */
  pairRow(
    key: string,
    label: string,
    request: ConfigurationFieldOf<C>,
    limit: ConfigurationFieldOf<C>,
    hint?: string,
  ): ReactNode;
  /** A volume's size: it only grows, from its current size. */
  sizeRow(field: ConfigurationFieldOf<C>, hint: string): ReactNode;
  /** A segmented control. */
  choice<K extends string>(options: ConfigurationChoice<K>): ReactNode;
}

/** A number of the configuration; a field of another type has none. */
const numberOf = (value: FieldValue): number | undefined =>
  typeof value === 'number' ? value : undefined;

/**
 * The parts of the rows of the « Configuration » tab, from the editor's
 * draft and rules: what the Odoo tab (`plugins/odoo`) and the generic tab
 * build their sections with, and what the shared sections
 * (`DatabaseSection`) are given.
 */
export function useConfigurationRows<C extends FieldValues>(
  input: ConfigurationRowsInput<C>,
): ConfigurationRows<C> {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const {
    idPrefix,
    ranges,
    draft,
    current,
    reference,
    referenceEnvironment,
    referenceLabel,
    locked,
  } = input;
  const lockedLabel = t('envPower.adminsOnly');
  const ids = (key: string) => `${idPrefix}-${key}`;

  const was = (...fields: Array<ConfigurationFieldOf<C>>) =>
    fields.some(field => draft[field] !== current[field])
      ? t('configuration.was', {
          value: fields
            .map(field => input.valueText(field, current[field]))
            .join(' / '),
        })
      : undefined;
  const hintText = (...parts: Array<string | undefined>) => {
    const text = parts.filter(Boolean).join(' · ');
    return text ? (
      <Text as="p" variant="body-small" color="secondary">
        {text}
      </Text>
    ) : undefined;
  };
  const referenceText = (text: (config: C) => string) => {
    if (!reference || !referenceEnvironment) return undefined;
    const value = text(reference);
    if (value === text(draft)) return undefined;
    return (
      <Text
        as="span"
        variant="body-small"
        className="waadoo-config__ref"
        data-tier={branchTier(referenceEnvironment)}
      >
        {t('configuration.reference', {
          branch: referenceLabel ?? referenceEnvironment,
          value,
        })}
      </Text>
    );
  };
  const fieldsText =
    (...fields: Array<ConfigurationFieldOf<C>>) =>
    (config: C) =>
      fields.map(field => input.valueText(field, config[field])).join(' / ');

  const number = (
    field: ConfigurationFieldOf<C>,
    labelledBy: string,
    min?: number,
  ) => {
    const range = ranges[field];
    const disabled = input.isDisabled(field);
    return (
      <NumberInput
        schema={{
          minimum: Math.max(range.min, min ?? range.min),
          maximum: range.max,
          multipleOf: range.step,
        }}
        value={numberOf(draft[field])}
        unit={input.unit(field) ?? ''}
        labelledBy={labelledBy}
        invalid={input.invalid(field)}
        isDisabled={disabled}
        // A field that cannot be edited shows its value as it is: snapped to
        // the caller's bounds, a locked 130 m would read 150 m. Disabled, it
        // is never committed nor natively validated (react-aria's
        // useNumberField skips a disabled input).
        commitBehavior={disabled ? 'validate' : 'snap'}
        onChange={value => {
          if (value !== undefined) input.set(field, value);
        }}
      />
    );
  };
  const pairRow = (
    key: string,
    label: string,
    request: ConfigurationFieldOf<C>,
    limit: ConfigurationFieldOf<C>,
    hint?: string,
  ) => {
    const labelId = ids(key);
    return (
      <ConfigurationRow
        key={key}
        label={label}
        labelId={labelId}
        locked={locked.has(request) || locked.has(limit)}
        lockedLabel={lockedLabel}
        hint={hintText(was(request, limit), hint)}
        reference={referenceText(fieldsText(request, limit))}
        problems={input.problems(request, limit)}
      >
        {number(request, labelId)}
        <span aria-hidden="true">/</span>
        {number(limit, labelId)}
      </ConfigurationRow>
    );
  };
  const choice = <K extends string>(options: ConfigurationChoice<K>) => (
    <ToggleButtonGroup
      aria-label={options.label}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[options.selected]}
      isDisabled={options.disabled}
      onSelectionChange={keys => {
        const [selected] = keys;
        const item = options.items.find(entry => entry.id === selected);
        if (item) options.onSelect(item.id);
      }}
      className="waadoo-config__choice waadoo-pill-segmented"
    >
      {options.items.map(item => (
        <ToggleButton key={item.id} id={item.id} size="small">
          {item.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
  const sizeRow = (field: ConfigurationFieldOf<C>, hint: string) => (
    <ConfigurationRow
      key={field}
      label={input.label(field)}
      labelId={ids(field)}
      locked={locked.has(field)}
      lockedLabel={lockedLabel}
      hint={
        <Text as="p" variant="body-small" color="secondary">
          {[was(field), hint].filter(Boolean).join(' — ')}
        </Text>
      }
      reference={referenceText(fieldsText(field))}
      problems={input.problems(field)}
    >
      {number(field, ids(field), numberOf(current[field]))}
    </ConfigurationRow>
  );

  return {
    ...input,
    ids,
    lockedLabel,
    hintText,
    was,
    referenceText,
    fieldsText,
    number,
    pairRow,
    choice,
    sizeRow,
  };
}
